import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/app-shell';
import { routes } from '@/config/routes';
import { ORDERS_PAGE_NAME, ORDERS_ROLES } from '@/features/orders';

import { RequireAuth } from './guards/require-auth';
import { RequireRole } from './guards/require-role';

const lazy =
  <K extends string>(loader: () => Promise<Record<K, ComponentType>>, key: K) =>
  async () => {
    const m = await loader();
    return { Component: m[key] };
  };

export const protectedRoutes: RouteObject[] = [
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            path: '/',
            lazy: lazy(() => import('@/pages/dashboard/dashboard.page'), 'DashboardPage'),
          },

          {
            element: <RequireRole roles={ORDERS_ROLES} pageName={ORDERS_PAGE_NAME} />,
            children: [
              {
                path: '/orders',
                lazy: lazy(() => import('@/pages/orders/orders-list.page'), 'OrdersListPage'),
              },
              {
                path: '/orders/:id',
                lazy: lazy(() => import('@/pages/orders/order-detail.page'), 'OrderDetailPage'),
              },
              {
                path: '/orders/:id/edit',
                lazy: lazy(() => import('@/pages/orders/order-edit.page'), 'OrderEditPage'),
              },
            ],
          },

          {
            path: '/customers',
            lazy: lazy(() => import('@/pages/customers/customers-list.page'), 'CustomersListPage'),
          },
          {
            path: '/customers/new',
            lazy: lazy(
              () => import('@/pages/customers/customer-create.page'),
              'CustomerCreatePage',
            ),
          },
          {
            path: '/customers/:id/edit',
            lazy: lazy(() => import('@/pages/customers/customer-edit.page'), 'CustomerEditPage'),
          },
          {
            path: '/customers/:id',
            lazy: lazy(
              () => import('@/pages/customers/customer-detail.page'),
              'CustomerDetailPage',
            ),
          },

          {
            element: <RequireRole roles={['admin', 'super_admin']} pageName="Product Management" />,
            children: [
              {
                path: routes.products,
                lazy: lazy(() => import('@/pages/products/products-hub.page'), 'ProductsHubPage'),
                children: [
                  { index: true, element: <Navigate to={routes.productsDevices} replace /> },
                  {
                    path: 'devices',
                    lazy: lazy(
                      () => import('@/pages/devices/devices-list.page'),
                      'DevicesListPage',
                    ),
                  },
                  {
                    path: 'components',
                    lazy: lazy(
                      () => import('@/pages/products/components-list.page'),
                      'ComponentsListPage',
                    ),
                  },
                  {
                    path: 'category',
                    lazy: lazy(
                      () => import('@/pages/products/categories-list.page'),
                      'CategoriesListPage',
                    ),
                  },
                  {
                    path: 'bundles',
                    lazy: lazy(
                      () => import('@/pages/products/bundles-list.page'),
                      'BundlesListPage',
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: '/products/:id/edit',
            lazy: lazy(() => import('@/pages/products/product-edit.page'), 'ProductEditPage'),
          },

          {
            element: <RequireRole roles={['admin', 'super_admin']} pageName="Product Management" />,
            children: [
              {
                path: routes.productsDevicesNew,
                lazy: lazy(() => import('@/pages/devices/device-create.page'), 'DeviceCreatePage'),
              },
              {
                path: routes.productsBundlesNew,
                lazy: lazy(() => import('@/pages/products/bundle-create.page'), 'BundleCreatePage'),
              },
              {
                path: routes.productsBundlesEdit(),
                lazy: lazy(() => import('@/pages/products/bundle-edit.page'), 'BundleEditPage'),
              },
              {
                path: routes.productsDevicesEdit(),
                lazy: lazy(() => import('@/pages/devices/device-edit.page'), 'DeviceEditPage'),
              },
              {
                path: routes.productsComponentsNew,
                lazy: lazy(
                  () => import('@/pages/components/component-create.page'),
                  'ComponentCreatePage',
                ),
              },
              {
                path: routes.productsComponentsEdit(),
                lazy: lazy(
                  () => import('@/pages/components/component-edit.page'),
                  'ComponentEditPage',
                ),
              },
            ],
          },

          {
            path: routes.requests,
            lazy: lazy(() => import('@/pages/requests/request-hub.page'), 'RequestHubPage'),
            children: [
              { index: true, element: <Navigate to={routes.requestsInstallation} replace /> },
              {
                path: 'installation',
                lazy: lazy(
                  () => import('@/pages/requests/installations-list.page'),
                  'InstallationsListPage',
                ),
              },
              {
                path: 'replacement',
                lazy: lazy(
                  () => import('@/pages/requests/replacements-list.page'),
                  'ReplacementsListPage',
                ),
              },
              {
                path: 'warranty',
                lazy: lazy(() => import('@/pages/requests/warranty-list.page'), 'WarrantyListPage'),
              },
            ],
          },

          {
            path: '/installations',
            lazy: lazy(
              () => import('@/pages/installations/installation-queue.page'),
              'InstallationQueuePage',
            ),
          },
          {
            path: '/installations/:id',
            lazy: lazy(
              () => import('@/pages/installations/installation-detail.page'),
              'InstallationDetailPage',
            ),
          },

          {
            path: '/devices',
            lazy: lazy(() => import('@/pages/devices/devices-list.page'), 'DevicesListPage'),
          },
          {
            path: '/devices/:id',
            lazy: lazy(() => import('@/pages/devices/device-detail.page'), 'DeviceDetailPage'),
          },

          {
            path: routes.support,
            lazy: lazy(() => import('@/pages/support/support-hub.page'), 'SupportHubPage'),
            children: [
              { index: true, element: <Navigate to={routes.supportOrders} replace /> },
              {
                path: 'orders',
                lazy: lazy(
                  () => import('@/pages/support/orders-tickets.page'),
                  'OrdersTicketsPage',
                ),
              },
              {
                path: 'devices',
                lazy: lazy(
                  () => import('@/pages/support/devices-tickets.page'),
                  'DevicesTicketsPage',
                ),
              },
            ],
          },

          {
            path: '/subscriptions',
            lazy: lazy(
              () => import('@/pages/subscriptions/subscriptions-list.page'),
              'SubscriptionsListPage',
            ),
          },
          {
            path: '/payments',
            lazy: lazy(() => import('@/pages/payments/payments-list.page'), 'PaymentsListPage'),
          },

          {
            path: routes.reports,
            lazy: lazy(() => import('@/pages/reports/reports-hub.page'), 'ReportsHubPage'),
            children: [
              { index: true, element: <Navigate to={routes.reportOrders} replace /> },
              {
                path: 'orders',
                lazy: lazy(() => import('@/pages/reports/orders-report.page'), 'OrdersReportPage'),
              },
              {
                path: 'device-count',
                lazy: lazy(
                  () => import('@/pages/reports/device-count-report.page'),
                  'DeviceCountReportPage',
                ),
              },
              {
                path: 'device-activity',
                lazy: lazy(
                  () => import('@/pages/reports/device-activity-report.page'),
                  'DeviceActivityReportPage',
                ),
              },
              {
                path: 'customer-usage',
                lazy: lazy(
                  () => import('@/pages/reports/customer-usage-report.page'),
                  'CustomerUsageReportPage',
                ),
              },
              {
                path: 'subscription',
                lazy: lazy(
                  () => import('@/pages/reports/subscription-report.page'),
                  'SubscriptionReportPage',
                ),
              },
            ],
          },

          {
            path: '/notifications',
            lazy: lazy(
              () => import('@/pages/notifications/notifications.page'),
              'NotificationsPage',
            ),
          },
          {
            path: '/activity-log',
            lazy: lazy(() => import('@/pages/activity-log/activity-log.page'), 'ActivityLogPage'),
          },

          {
            element: <RequireRole roles={['admin', 'super_admin']} pageName="Settings" />,
            children: [
              {
                path: '/configurations/tax',
                lazy: lazy(() => import('@/pages/configurations/tax-config.page'), 'TaxConfigPage'),
              },
              {
                path: '/configurations/discounts',
                lazy: lazy(() => import('@/pages/configurations/discounts.page'), 'DiscountsPage'),
              },
              {
                path: '/configurations/discounts/new',
                lazy: lazy(
                  () => import('@/pages/configurations/discount-create.page'),
                  'DiscountCreatePage',
                ),
              },
              {
                path: '/configurations/discounts/:id/edit',
                lazy: lazy(
                  () => import('@/pages/configurations/discount-edit.page'),
                  'DiscountEditPage',
                ),
              },
              {
                path: '/configurations/discounts/:id',
                lazy: lazy(
                  () => import('@/pages/configurations/discount-detail.page'),
                  'DiscountDetailPage',
                ),
              },
              {
                path: '/configurations/installation-fees',
                lazy: lazy(
                  () => import('@/pages/configurations/installation-fees.page'),
                  'InstallationFeesPage',
                ),
              },
              {
                path: '/configurations/warranty-periods',
                lazy: lazy(
                  () => import('@/pages/configurations/warranty-periods.page'),
                  'WarrantyPeriodsPage',
                ),
              },
            ],
          },
          {
            element: <RequireRole roles={['admin', 'super_admin']} pageName="Internal Users" />,
            children: [
              {
                path: routes.adminUsers,
                lazy: lazy(
                  () => import('@/pages/user-management/internal-users-hub.page'),
                  'InternalUsersHubPage',
                ),
                children: [
                  { index: true, element: <Navigate to={routes.adminUsersInternal} replace /> },
                  {
                    path: 'internal',
                    lazy: lazy(
                      () => import('@/pages/user-management/admin-users-list.page'),
                      'AdminUsersListPage',
                    ),
                  },
                  {
                    path: 'dealers',
                    lazy: lazy(
                      () => import('@/pages/user-management/dealers-list.page'),
                      'DealersListPage',
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
